import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactPlayer from 'react-player';
import MediaControls from './MediaControls';
import Transcript from './Transcript';

const VideoWrapper = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 3rem auto 1rem;
  position: relative;
  .video__header {
    font-size: 1rem;
    line-height: 1.25rem;
    color: white;
    margin-bottom: 0.5rem;
    span {
      color: var(--custom-red);
      text-transform: uppercase;
    }
    @media (max-width: 1000px) {
      padding: 0 20px;
    }
  }
  @media (max-aspect-ratio: 9/16) and (max-width: 1040px) {
    width: 100%;
    max-width: none;
  }
`;

const VimeoBlock = ({
  data,
  windowWidth,
  windowHeight,
  videoData,
  updateVideoData,
  audio,
  enableAudio,
  playing,
  setPlaying,
}) => {
  const videoWrapperRef = useRef(null);
  const videoRef = useRef(null);
  const videoIndex = useRef(videoData.findIndex((a) => a.id === data.videoId));
  const progressRef = useRef();
  const hasBeenPaused = useRef(false);
  const transcriptRef = useRef(false);
  const [videoWrapperWidth, setVideoWrapperWidth] = useState();
  const [showVertical, setShowVertical] = useState(false);

  // ---------------------------------------------------------------
  // controlling video format
  // ---------------------------------------------------------------
  // if windowWidth is less than/equal to 1040 and the aspect ration is less
  // than 16/9, switch to a vertical format for video, else, use a horiztonal

  useEffect(() => {
    setVideoWrapperWidth(videoWrapperRef.current.offsetWidth);
    if (windowWidth <= 1040 && windowWidth / windowHeight <= 0.75) {
      setShowVertical(true);
    } else {
      setShowVertical(false);
    }
  }, [windowWidth, windowHeight]);

  // ---------------------------------------------------------------
  // onDuration
  // ---------------------------------------------------------------
  // runs when the video file's metadata is loaded
  // updates video data with duration of video file

  const checkDuration = (duration) => {
    const clonedVideoData = [...videoData];
    clonedVideoData[videoIndex.current].duration = duration;
    updateVideoData(clonedVideoData);
  };

  // ---------------------------------------------------------------
  // onEnded
  // ---------------------------------------------------------------
  // runs when the video ends

  const videoEnded = () => {
    setPlaying('');
  };

  // ---------------------------------------------------------------
  // progress update
  // ---------------------------------------------------------------
  // runs when the current time on the movie changes. updates the video
  // data with the current time on the corresponding video.

  const checkProgress = useCallback((d) => {
    const clonedVideoData = [...videoData];
    const { playedSeconds } = d;
    clonedVideoData[videoIndex.current].currentTime = playedSeconds;
    updateVideoData(clonedVideoData);
  }, [videoData]);

  // ---------------------------------------------------------------
  // playing the video
  // ---------------------------------------------------------------
  // runs when the play/pause button for the corresponding video is clicked
  // if the playing state is an empty string, it's set to the corresponding
  // video id. if not, it's set to an empty string. The VideoWrapper element's
  // playing prop is a boolean that is set to true if this state matches
  // the corresponding video id.

  const playVideo = useCallback(() => {
    if (playing === '' || playing !== videoData[videoIndex.current].id) {
      setPlaying(videoData[videoIndex.current].id);
      hasBeenPaused.current = false;
    } else {
      setPlaying('');
      hasBeenPaused.current = true;
    }
  }, [playing]);

  // ---------------------------------------------------------------
  // range slider on change callback
  // ---------------------------------------------------------------
  // updates video file's current time when range slider is changed

  const changeTime = useCallback(() => {
    videoRef.current.seekTo(progressRef.current.value / 1000);
    setPlaying(videoData[videoIndex.current].id);
  }, [videoData, videoRef.current, progressRef.current]);

  // ---------------------------------------------------------------
  // transcript toggle
  // ---------------------------------------------------------------
  // updates video file's transcriptViewable property

  const toggleTranscript = useCallback(() => {
    const clonedVideoData = [...videoData];
    transcriptRef.current = !transcriptRef.current;
    clonedVideoData[videoIndex.current].transcriptViewable = transcriptRef.current;
    updateVideoData(clonedVideoData);
  }, []);

  const inlineAudioToggle = useCallback(() => {
    enableAudio(false);
    setPlaying('');
  }, []);

  useEffect(() => {
    function handleIntersect(entries) {
      entries.forEach((entry) => {
        if (audio) {
          if (entry.isIntersecting && hasBeenPaused.current !== true) {
            setPlaying(videoData[videoIndex.current].id);
          } else {
            setPlaying('');
          }
        }
      });
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2,
    };

    const observer = new IntersectionObserver(handleIntersect, options);
    if (videoWrapperRef.current) {
      observer.observe(videoWrapperRef.current);
    }
    return () => observer.unobserve(videoWrapperRef.current);
  }, [videoWrapperRef.current, audio, hasBeenPaused.current]);

  return (
    <React.Fragment>
      <VideoWrapper ref={videoWrapperRef}>
        <h6 className="bentonsanscond-n6 video__header"><span>Watch:</span> {videoData[videoIndex.current].description}</h6>
        {videoWrapperWidth && (
          <ReactPlayer
            url={showVertical === false ? `https://vimeo.com/${data.vimeoHorizontal}` : `https://vimeo.com/${data.vimeoVertical}`}
            onDuration={checkDuration}
            onProgress={(d) => checkProgress(d)}
            onEnded={videoEnded}
            width={videoWrapperWidth}
            height={showVertical === false ? videoWrapperWidth * 0.562 : videoWrapperWidth * 1.8}
            ref={videoRef}
            progressInterval={50}
            playing={videoData[videoIndex.current].id === playing}
          />

        )}

        {videoRef.current !== null && (
          <MediaControls
            playMedia={playVideo}
            maxDuration={videoData[videoIndex.current].duration}
            currentTime={videoRef.current.getCurrentTime()}
            changeTime={changeTime}
            playing={playing}
            progressRef={progressRef}
            mediaId={videoData[videoIndex.current].id}
            mediaType="video"
          />
        )}

      </VideoWrapper>

      <Transcript
        transcriptViewable={videoData[videoIndex.current].transcriptViewable}
        toggleTranscript={toggleTranscript}
        inlineAudioToggle={inlineAudioToggle}
        transcript={videoData[videoIndex.current].transcript}
        audio={audio}
        videoTranscript
      />
    </React.Fragment>
  );
};

VimeoBlock.propTypes = {
  data: PropTypes.shape().isRequired,
  windowWidth: PropTypes.number.isRequired,
  windowHeight: PropTypes.number.isRequired,
  videoData: PropTypes.arrayOf(
    PropTypes.shape(),
  ).isRequired,
  updateVideoData: PropTypes.func.isRequired,
  audio: PropTypes.bool.isRequired,
  enableAudio: PropTypes.func.isRequired,
  playing: PropTypes.string.isRequired,
  setPlaying: PropTypes.func.isRequired,
};

export default VimeoBlock;
